import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { useAuth } from '../api/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { newExportObjectAsPDF } from "../util/NewExportPdf";
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import DWRForm from './DWRForm';

const appUrl = process.env.REACT_APP_APP_URL;

if (!appUrl) {
  throw new Error("REACT_APP_APP_URL is not defined. Please set it in your .env file.");
}

const RecentlyApprovedDWRs = () => {
  const { authState } = useAuth();
  const [dwrs, setDwrs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDwr, setSelectedDwr] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const token = authState?.token;
  const navigate = useNavigate();
  const location = useLocation();

  const fetchApprovedDWRs = async () => {
    setIsLoading(true);
    try {
      // Calculate date 48 hours ago
      const fortyEightHoursAgo = new Date();
      fortyEightHoursAgo.setHours(fortyEightHoursAgo.getHours() - 48);
      const isoDate = fortyEightHoursAgo.toISOString();
      
      // Use query parameters to filter on the backend
      const response = await axios.get(`${appUrl}/jmrapp/dailyworkrecords/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          is_approved: true,
          approved_after: isoDate
        }
      });
      
      setDwrs(response.data);
      console.log('Found', response.data.length, 'DWRs approved in the last 48 hours');
    } catch (error) {
      console.error("Failed to fetch DWRs:", error);
      confirmAlert({
        title: "Error",
        message: "Failed to load DWRs. Please try again.",
        buttons: [{ label: "OK" }],
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (authState.authenticated) {
      fetchApprovedDWRs();
    }
  }, [authState.authenticated]);
  
  const handleRefresh = () => {
    fetchApprovedDWRs();
  };

  const handleDwrClick = async (dwr) => {
    try {
      const token = localStorage.getItem('my-jwt');
      const dwrResponse = await axios.get(`${appUrl}/jmrapp/dailyworkrecords/${dwr.id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const wellId = dwrResponse.data.well.id;
      const wellResponse = await axios.get(`${appUrl}/jmrapp/wells/${wellId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const combinedData = {
        ...dwrResponse.data,
        well: wellResponse.data
      };

      // Update the location state with well information
      navigate('.', { 
        state: { ...location.state, well: wellResponse.data }, 
        replace: true 
      });
      
      setSelectedDwr(combinedData);
      setShowForm(true);
    } catch (error) {
      console.error('Failed to fetch full DWR data:', error);
      confirmAlert({
        title: "Error",
        message: "Failed to load DWR data. Please try again.",
        buttons: [{ label: "OK" }],
      });
    }
  };

  const handleCloseForm = () => {
    setSelectedDwr(null);
    setShowForm(false);
    fetchApprovedDWRs(); // Refresh the DWR list when the form is closed
  };

  const handlePDFDownload = async (dwr, event) => {
    event.stopPropagation();
    try {
      const fullDwrData = await fetchFullDWRData(dwr.id);
      await newExportObjectAsPDF(fullDwrData, token);
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      confirmAlert({
        title: "Error",
        message: "Failed to generate PDF. Please try again.",
        buttons: [{ label: "OK" }],
      });
    }
  };

  const fetchFullDWRData = async (dwrId) => {
    try {
      const response = await axios.get(`${appUrl}/jmrapp/dailyworkrecords/${dwrId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch full DWR data for ID ${dwrId}:`, error);
      throw error;
    }
  };

  const columns = [
    { field: 'date', headerName: 'Date', flex: 1, filterable: true },
    { field: 'well_name', headerName: 'Well', flex: 1, filterable: true },
    { field: 'rig_name', headerName: 'Rig', flex: 1, filterable: true },
    { 
      field: 'approved_at', 
      headerName: 'Approval Date', 
      flex: 1,
      valueGetter: (params) => {
        if (!params.row.approved_at) return 'N/A';
        const date = new Date(params.row.approved_at);
        return `${date.toLocaleString("en-US", {
          timeZone: "America/Chicago",
          dateStyle: "short",
          timeStyle: "short"
        })} (CT)`;
      }
    },
    {
      field: 'approved_by',
      headerName: 'Approved By',
      flex: 1,
      valueGetter: (params) => {
        const approver = params.row.approved_by;
        if (!approver) return 'N/A';
        return `${approver.first_name} ${approver.last_name}`;
      }
    },
    {
      field: 'so_created_at',
      headerName: 'SO Created At',
      flex: 1,
      valueGetter: (params) =>
        params.row.so_created_at
          ? `${new Date(params.row.so_created_at).toLocaleString("en-US", {
              timeZone: "America/Chicago",
              dateStyle: "short",
              timeStyle: "short"
            })} (CT)`
          : 'N/A'
    },
    {
      field: 'pdf',
      headerName: 'PDF',
      flex: 0.5,
      align: 'left',
      renderCell: (params) => (
        <button onClick={(event) => handlePDFDownload(params.row, event)}>
          <FileDownloadIcon />
        </button>
      ),
    },
  ];

  return (
    <div style={{ height: '75vh', width: '100%' }}>
      <Box sx={{ 
        p: 2, 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }}>
        <div>
          <Typography variant="h6">
            Recently Approved DWRs
          </Typography>
          {isLoading ? (
            <CircularProgress size={24} sx={{ ml: 2 }} />
          ) : (
            <Typography variant="body2" sx={{ ml: 2 }}>
              {dwrs.length} DWRs approved in the last 48 hours
            </Typography>
          )}
        </div>
        <Button 
          variant="contained" 
          onClick={handleRefresh} 
          disabled={isLoading}
          sx={{ width: '100px' }}
        >
          Refresh
        </Button>
      </Box>

      {showForm ? (
        <div>
          <DWRForm
            selectedDwr={selectedDwr}
            setSelectedDwr={setSelectedDwr}
            onClose={handleCloseForm}
            setShowForm={setShowForm}
            onSubmit={handleCloseForm}
          />
          <button className="btn close-btn" onClick={handleCloseForm}>
            Close Form
          </button>
        </div>
      ) : (
        <DataGrid
          rows={dwrs}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 25, page: 0 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          loading={isLoading}
          onRowClick={(params) => handleDwrClick(params.row)}
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell': {
              whiteSpace: 'normal',
              lineHeight: 'normal',
              padding: '8px',
            },
            '& .MuiDataGrid-row': {
              maxHeight: 'none !important',
            },
          }}
        />
      )}
    </div>
  );
};

export default RecentlyApprovedDWRs; 
